import * as z from "zod"
import { ConsentCode } from "@/entities/consents"
import {
    BooleanField,
    consentOrder,
    ConsentValue,
    SMSCommunicationPreference,
} from "../constants/consents"
import { type NormalizedConsents } from "../types/consentType"

export const ConsentSchema = z.object({
    code: z.enum(ConsentCode),
    id: z.number(),
    value: z.enum(ConsentValue),
})

/**
 * Normalizer for the consents list
 * @description This function normalizes the consents list by splitting the code into prefix and subcode.
 * It also replaces the "-" with "_" in the subcode. It was causing string parsing issues for next-intl.
 * The result is an object where the keys are the prefixes and the values are arrays of objects with the subcode and other properties.
 * @param data - The consents list to normalize
 * @example
 * [
 *  {
 *   code: "marketing.commercial-communication",
 *   id: 1,
 *   value: "ACCEPTED",
 *  },
 * ]
 * @returns
 * {
 *  "marketing": [
 *   {
 *    code: "commercial_communication",
 *    id: 1,
 *    value: "ACCEPTED",
 *   },
 * ],
 */
export const ConsentsNormalizer = ConsentSchema.array().transform((consents) => {
    const result: NormalizedConsents = {}

    consents.forEach((consent) => {
        const [prefix, subcode] = consent.code.split(".")

        if (!result[prefix]) {
            result[prefix] = []
        }

        result[prefix].push({
            ...consent,
            order: consentOrder[consent.code],
            code: subcode.replaceAll("-", "_"),
        })
    })

    return result
})

const PreferenceSchema = z.object({
    [SMSCommunicationPreference]: z.coerce.boolean().optional(),
})

export const PreferencesNormalizer = PreferenceSchema.transform((data) => {
    return {
        code: SMSCommunicationPreference, // random string to match ConsentSchema
        id: 1, // random number to match ConsentSchema
        value: data[SMSCommunicationPreference] ? ConsentValue.ACCEPTED : ConsentValue.REJECTED,
        order: 4,
    }
})

export const CommunicationPreferencesFormSchema = z.record(z.string(), z.enum(BooleanField))

export type CommunicationPreferencesFormValues = z.infer<typeof CommunicationPreferencesFormSchema>

const PreferencesSchema = z.object({
    code: z.string(),
    id: z.number(),
    value: z.enum(ConsentValue),
})

export const CommunicationPreferencesParserSchema = z.array(PreferencesSchema).transform((data) => {
    const result: Record<string, BooleanField> = {}

    for (const consent of data) {
        result[consent.code] =
            ConsentValue.ACCEPTED === consent.value ? BooleanField.YES : BooleanField.NO
    }

    return result
})

export function ConsentKeyDenormalizer(key: string) {
    return `marketing.${key.replace("_", "-")}`
}

/**
 * @description This schema is used to transform the communication preferences form values into the format expected by the API.
 * Since the initial consentCode was updated to better handle stringification, we need to transform back the data.
 * @example
 * {
 * "marketing.commercial-communication": "YES",
 *
 */
export const ConsentsDenormalizer = CommunicationPreferencesFormSchema.transform((data) => {
    return Object.entries(data).map(([consentKey, consentValue]) => {
        return {
            consentTypeCode: ConsentKeyDenormalizer(consentKey),
            value:
                consentValue === BooleanField.YES ? ConsentValue.ACCEPTED : ConsentValue.REJECTED,
        }
    })
})
